// Route guard for admin-only screens.
//
// SECURITY MODEL
// --------------
// Frontend gating here is purely UX — it hides admin chrome from
// non-admins. Real protection lives in the database:
//   - admin_users table is read-only and gated by RLS (is_admin())
//   - Sensitive admin RPCs (get_kpi_data, delete_user, update_user_email,
//     mark_contact_message_read, etc.) all run RAISE EXCEPTION on a
//     server-side admin_users lookup before any work happens
//   - Tables exposed to admins (contact_messages, app_settings,
//     broadcast_popups, banned_users, …) use is_admin() in their RLS
//     policies, so a non-admin who bypasses this guard still sees nothing
//
// This component performs an additional server round-trip via
// isAdminUser() (which queries admin_users with RLS enforced) so we don't
// trust any cached client state.
import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isAdminUser } from "@/lib/identity";

type Status = "checking" | "allowed" | "denied" | "no-session";

export default function RequireAdmin({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");
  const loc = useLocation();

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      // Trust the auth server (re-validates the token) — not cached state.
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user || user.is_anonymous) { setStatus("no-session"); return; }
      const ok = await isAdminUser(user.email);
      if (cancelled) return;
      setStatus(ok ? "allowed" : "denied");
    };

    verify();
    const { data: sub } = supabase.auth.onAuthStateChange(() => verify());
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  if (status === "checking") {
    return <div className="p-6 text-center lab-label">VERIFYING CLEARANCE…</div>;
  }

  if (status === "no-session") {
    return (
      <Panel
        title="AUTHENTICATION REQUIRED"
        body="THIS TERMINAL REQUIRES OPERATOR AUTHENTICATION."
        cta={{ to: `/auth`, label: "ENGAGE AUTHENTICATION", state: { from: loc.pathname } }}
      />
    );
  }

  if (status === "denied") {
    return (
      <Panel
        title="ACCESS DENIED"
        body="OPERATOR LACKS REQUIRED CLEARANCE FOR THIS SECTOR. INCIDENT LOGGED."
        cta={{ to: "/", label: "RETURN TO PROTOCOL TIMER" }}
      />
    );
  }

  return <>{children}</>;
}

function Panel({
  title, body, cta,
}: {
  title: string; body: string;
  cta: { to: string; label: string; state?: unknown };
}) {
  return (
    <div className="max-w-sm mx-auto pt-6">
      <section className="lab-panel">
        <div className="lab-strip flex justify-between">
          <span>{title}</span>
          <span className="text-[hsl(var(--terminal-red))]">SEC//L5</span>
        </div>
        <div className="p-3 space-y-3">
          <p className="text-[11px] leading-relaxed text-[hsl(var(--terminal-dim))] tracking-wide">{body}</p>
          <Link
            to={cta.to}
            state={cta.state as any}
            className="lab-btn lab-btn-primary block text-center text-[10px]"
          >
            {cta.label}
          </Link>
        </div>
      </section>
    </div>
  );
}
